import { AlertTriangle, Plus } from 'lucide-preact'
import type { UnmatchedPresenter } from '@/api/webs'
import { PersonAvatar } from '@/components/common/Avatar'
import { unmatchedPresentersHeading, unmatchedReasonText } from '@/utils/unmatchedPresenters'

interface UnmatchedPresentersBannerProps {
  presenters: UnmatchedPresenter[]
  /** Viewers see who is missing but get no actions: Add and Dismiss both need an editor. */
  canEdit: boolean
  /** Speakers are locked after sign-off, so a presenter can't be added; the list can still be dismissed. */
  addDisabled?: boolean
  dismissing?: boolean
  onAdd: (presenter: UnmatchedPresenter) => void
  onDismiss: () => void
}

/**
 * BigMarker presenters the speaker import couldn't add, because a speaker needs a LinkedIn-backed contact
 * (ORB-911). Each can be added from here with BigMarker's details already filled in; an entry leaves the
 * list once a speaker with that email is saved.
 */
export function UnmatchedPresentersBanner({ presenters, canEdit, addDisabled, dismissing, onAdd, onDismiss }: UnmatchedPresentersBannerProps) {
  const count = presenters.length
  return (
    <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300" role="status">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium">
            {unmatchedPresentersHeading(count)}
          </p>
          {canEdit && <p className="text-xs mt-0.5">Add each one with their LinkedIn URL. Their other details come from BigMarker.</p>}
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {presenters.map((presenter, index) => (
          <li key={`${presenter.Email ?? presenter.Name}-${index}`} className="flex items-center gap-3 rounded-md bg-white/70 dark:bg-slate-900/40 px-2 py-1.5">
            <PersonAvatar fullName={presenter.Name} profilePicUrl={presenter.PhotoUrl} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-slate-800 dark:text-slate-100">{presenter.Name}</p>
              <p className="truncate text-xs text-amber-700 dark:text-amber-400">{unmatchedReasonText(presenter)}</p>
            </div>
            {canEdit && <button
              type="button"
              onClick={() => onAdd(presenter)}
              disabled={addDisabled}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-2 flex items-center justify-end gap-2">
          {/* Dismiss hides the list for everyone with no way back, so say so before the click. */}
          <span className="text-xs text-amber-700 dark:text-amber-400">Hide this for good. Presenters stay in BigMarker.</span>
          <button type="button" onClick={onDismiss} disabled={dismissing} className="text-xs underline hover:no-underline disabled:opacity-50">
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      )}
    </div>
  )
}
